import type { FeatureResult } from '@growthbook/growthbook';
import AppPaths from '../../AppPaths';
import { hasTargetingConsent } from '../../lib/consent';
import { getDomainConfig } from '../../lib/domainConfig';

/**
 * String growthbook feature read on `/products?pdp=`. `plp` renders the listing, `pdp` opens the shop
 * narrowed to the named trackers, `site` sends the visitor to the tracker's marketing-site page.
 * One experiment rule per ad cell, targeted on the `url` attribute.
 */
export const LANDING_ARM_FLAG = 'ecom-landing-arm';
export const DEFAULT_LANDING_ARM = 'plp';

const DEVICES = ['mini', 's3', 'ultra', 'cat'];

// the marketing site's story page per tracker; a multi-tracker list has none and gets the homepage
const SITE_PAGES: Record<string, string> = { mini: '/mini/dog', cat: '/mini/cat', s3: '/collar', ultra: '/ultra' };

/** The trackers named by `pdp=` (comma or hyphen separated), unknown keys dropped. Empty when the param is absent. */
export function parsePdpParam(search: string) {
  const raw = new URLSearchParams(search).get('pdp') ?? '';
  return raw
    .toLowerCase()
    .split(/[,-]/)
    .map((key) => key.trim())
    .filter((key, i, all) => DEVICES.includes(key) && all.indexOf(key) === i);
}

/** The ad's own params (utm, click ids, coupons, `type=cat`) as sent, with only `pdp=` dropped. */
function adParams(search: string) {
  return search
    .replace(/^\?/, '')
    .split('&')
    .filter((pair) => pair && !pair.startsWith('pdp='));
}

function isCatsAd(devices: string[], params: string[]) {
  return devices[0] === 'cat' || params.includes('type=cat');
}

/**
 * The pdp arm: the shop narrowed to the named trackers with the first one open. `cat` is the mini
 * with the cats lineup. `lp=pdp` marks the session as this arm for the dashboard.
 */
export function resolvePdpDestination(devices: string[], search: string) {
  const params = adParams(search);
  const shop = devices.map((key) => (key === 'cat' ? 'mini' : key)).filter((key, i, all) => all.indexOf(key) === i);
  const own = [`products=${shop.join(',')}`, `selected=${shop[0]}`];
  if (isCatsAd(devices, params) && !params.includes('type=cat')) {
    own.push('type=cat');
  }
  return { pathname: AppPaths.Shop, search: `?${[...own, ...params, 'lp=pdp'].join('&')}` };
}

const UTM_CAMPAIGN_FIELDS = [
  ['name', 'utm_campaign'],
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['term', 'utm_term'],
  ['content', 'utm_content'],
];

export type LandingExposure = Pick<FeatureResult<unknown>, 'experiment' | 'experimentResult'>;

/**
 * Re-sends the landing-arm exposure as a sendBeacon shaped like analytics.js's own track message
 * (the analytics proxy fills in the write key). The site hop cancels segment's in-flight xhr and its
 * retry queue only drains on the next shop visit, so the site arm would lose every visitor who never
 * comes back. False when there is nothing to send or no way to send it.
 */
export function sendLandingExposureBeacon(feature: LandingExposure | undefined): boolean {
  const apiHost = process.env.REACT_APP_ANALYTICS_HOST;
  const experiment = feature?.experiment?.key;
  const result = feature?.experimentResult;
  if (!experiment || !result?.inExperiment || !apiHost || !navigator.sendBeacon) {
    return false;
  }
  const { href, pathname, search } = window.location;
  const now = new Date().toISOString();
  // analytics.js fills context.campaign from the utm params and the warehouse keys on it (utm_source etc.)
  const utm = new URLSearchParams(search);
  const campaign = Object.fromEntries(
    UTM_CAMPAIGN_FIELDS.filter(([, param]) => utm.has(param)).map(([field, param]) => [field, utm.get(param)]),
  );
  const message = {
    type: 'track',
    event: 'User Viewed Experiment',
    // the proxy drops events from non-consented visitors in opt-in regions on this flag, as the wrapper sends it
    properties: { name: experiment, variant: String(result.variationId), trackingConsented: hasTargetingConsent() },
    anonymousId: window.analytics.user().anonymousId(),
    userId: null,
    writeKey: '',
    timestamp: now,
    sentAt: now,
    context: {
      page: { url: href, path: pathname, search, referrer: document.referrer, title: document.title },
      campaign,
      userAgent: navigator.userAgent,
      library: { name: 'analytics.js', version: 'beacon' },
    },
    integrations: {},
  };
  return navigator.sendBeacon(`https://${apiHost}/t`, JSON.stringify(message));
}

/** The site arm: the tracker's marketing page, ad params forwarded so utm and fbclid survive the hop. */
export function resolveSiteDestination(devices: string[], search: string) {
  const params = adParams(search);
  const key = devices.length === 1 ? devices[0] : '';
  const page = key === 'mini' && isCatsAd(devices, params) ? SITE_PAGES.cat : (SITE_PAGES[key] ?? '/');
  return `https://${getDomainConfig().baseDomain}${page}?${[...params, 'lp=site'].join('&')}`;
}
